///
/// EarthBrowser
///
/// Created by Matt Giger on 3/23/2024
/// Copyright (c) 2024 Geodata Labs
///

import { Camera, type CameraControllerProtocol } from "@stovokor/camera/Camera"
import { Perspective } from "@stovokor/camera/Perspective"
import { type Light } from "@stovokor/engine/Light"
import { RenderState } from "@stovokor/engine/RenderState"
import { type RenderActionSet } from "@stovokor/engine/Renderer"
import { GestureNode } from "@stovokor/gestures/GestureNode"
import { GestureHitResult } from "@stovokor/gestures/GestureRecognizer"

export interface ViewportInterface {
	readonly camera: Camera
	readonly width: number
	readonly height: number
	gestureHitTest(state: RenderState): GestureHitResult[]
}

export class WorldViewport extends GestureNode implements ViewportInterface {
	public readonly id = "viewport"
	public readonly focusable = false

	public readonly camera = new Camera(new Perspective())

	private _width: number = 0
	private _height: number = 0
	public get width(): number { return this._width }
	public get height(): number { return this._height }

	private lights: Light[] = []

	// Legacy support for old event-based system (deprecated)
	private currentController?: CameraControllerProtocol

	// Reused across frames: the render state (stacks, frustum) and the two
	// pass lists, so collection allocates only what the nodes return
	private readonly renderState = new RenderState(this.camera)
	private readonly opaque: RenderActionSet[] = []
	private readonly transparent: RenderActionSet[] = []

	public size(width: number, height: number): void {
		this._width = width
		this._height = height

		if (width > 0 && height > 0) {
			this.camera.projection.setSize(width, height)
			this.camera.updateProjection()
		}
	}

	public addLight(light: Light): void {
		this.lights.push(light)
	}

	public removeLight(light: Light): void {
		const index = this.lights.indexOf(light)
		if (index >= 0) {
			this.lights.splice(index, 1)
		}
	}

	/**
	 * Per-tick scene update, run by the engine loop whether or not this
	 * tick draws a frame: focus/follow logic that moves the camera, and the
	 * camera matrices. Returns true when the camera changed, so the loop
	 * knows a redraw is needed
	 */
	public update(): boolean {
		// Update focused node (for animations, following, etc.)
		this.context.focusManager.update()

		// Legacy: Also call old controller if present (during migration)
		this.currentController?.renderUpdate?.()

		return this.camera.updateProjection()
	}

	// set up the viewport, clear the screen and render the 3d and 2d elements
	public renderViewport(): void {
		if (!this.visible) {
			return
		}

		const gl = this.context.gl

		// The viewport spans the whole drawing buffer, which is the CSS size
		// times the pixel ratio; the label and UI viewports draw into it too
		this.context.glState.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight)
		gl.clearColor(0, 0, 0, 1)
		gl.clear(WebGL2RenderingContext.COLOR_BUFFER_BIT | WebGL2RenderingContext.DEPTH_BUFFER_BIT)

		const state = this.renderState
		state.beginFrame(this.camera, this.context.frameTime)
		state.pixelRatio = this.context.pixelRatio
		const actions: RenderActionSet[] = this.renderChildren(state)

		// Single partition pass into the reused lists
		const opaque = this.opaque
		const transparent = this.transparent
		for(let i = 0; i < actions.length; i++) {
			const set = actions[i]
			if(set.transparency) {
				transparent.push(set)
			} else {
				opaque.push(set)
			}
		}

		const glState = this.context.glState
		glState.passDepthWrite = true
		glState.depthMask(true)
		// Opaque grouped by program (fewer program/VAO switches), near-to-far
		// within a group so the depth test rejects hidden fragments early
		opaque.sort((a, b) => (a.order - b.order) || (a.program - b.program) || (a.depth - b.depth))
		for(let i = 0; i < opaque.length; i++) {
			opaque[i].render()
		}

		// Disable depth writing for transparent objects to prevent incorrect occlusion
		// They still read from depth buffer (depth test) but don't write to it.
		// Far-to-near so nearer surfaces blend over the ones behind them
		glState.passDepthWrite = false
		glState.depthMask(false)
		transparent.sort((a, b) => (a.order - b.order) || (b.depth - a.depth))
		for(let i = 0; i < transparent.length; i++) {
			transparent[i].render()
		}
		glState.passDepthWrite = true
		glState.depthMask(true)
		opaque.length = 0
		transparent.length = 0
		// Leave the default vertex array bound: code drawing outside a
		// renderer (offscreen bakers) sets attributes on whatever is bound
		glState.bindVertexArray(null)
	}
}
